import React, { useMemo } from 'react';
import { Field } from 'formik';
import { getIntl } from '../../../../utils/intl';
import getName from '../../../../utils/getName';
import useOrganizationUsers from '../../../../hooks/api/useOrganizationUsers';
import useOrganization from '../../../../hooks/api/useOrganization';
import { useLoadedUser } from '../../../../hooks/api/useUser';
import { FormikInputs } from '../../../v2BaseComponents/Inputs';
import { SplitFormRow } from '../../../v2BaseComponents/Form';

const intl = getIntl('WithOrganizationUserField', {
    with: 'With team member',
});

/**
 * "With" select for org visit flows — lists organization users (signed-in user first)
 * and writes the chosen display name into resource.providerName.
 */
const WithOrganizationUserFieldInner: React.FC<{ isDisabled?: boolean }> = ({ isDisabled }) => {
    const { users, isLoadingUsers } = useOrganizationUsers({});
    const { user: currentUser } = useLoadedUser({});

    const selectOptions = useMemo(() => {
        const sorted = [...(users || [])].sort((a, b) => {
            if (a.user.userId === currentUser.userId) return -1;
            if (b.user.userId === currentUser.userId) return 1;
            return (getName(a.user) || '').localeCompare(getName(b.user) || '');
        });
        return sorted
            .map((member) => getName(member.user))
            .filter((name): name is string => !!name)
            .map((name) => ({ label: name, value: name, testID: name }));
    }, [users, currentUser.userId]);

    return (
        <SplitFormRow>
            <Field
                component={FormikInputs.Select}
                name="resource.providerName"
                title={intl('with')}
                isDisabled={isDisabled || isLoadingUsers}
                selectOptions={selectOptions}
            />
        </SplitFormRow>
    );
};

/** Renders nothing when the signed-in user has no organization (family app safety). */
const WithOrganizationUserField: React.FC<{ isDisabled?: boolean }> = (props) => {
    const { myOrganization } = useOrganization({});
    if (!myOrganization?.organization?.organizationId) return null;
    return <WithOrganizationUserFieldInner {...props} />;
};

export default React.memo(WithOrganizationUserField);
